import Button from "components/buttons/Button";
import { Modal } from "components/Modal/Modal";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import {
  getSocialNetworkItem,
  handleClearDraftItem,
  updateSocialNetworkItem,
  createSocialNetworkItem,
} from "store/cms/footer/socialNetworks/slice";
import { closeModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalTypes } from "configs/types";
import { selectFilteredGeo } from "store/geoFilter/selectors";

import styles from "./CmsFooterSocialNetworksEditModal.module.scss";

import SocialNetworksMainTab from "./tabs/SocialNetworksMainTab";
import SocialNetworksTrailTab from "./tabs/SocialNetworksTrailTab";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

enum ModalPartnersTabs {
  main = "main",
  trail = "trail",
}

const CmsFooterSocialNetworksEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const formErrors = useFormErrors();

  const isItemFetched = useAppSelector(
    (store) => store["cms-social-networks"].isItemFetched,
  );
  const isItemFetching = useAppSelector(
    (store) => store["cms-social-networks"].isItemFetching,
  );
  const geoList = useAppSelector(selectFilteredGeo);
  const draftItem = useAppSelector(
    (store) => store["cms-social-networks"].draftItem,
  );

  const [activeTab, setActiveTab] = useState<ModalPartnersTabs>(
    ModalPartnersTabs.main,
  );

  const newUploadedImages = useRef<{ id: number; image: File }[]>([]);

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getSocialNetworkItem(+entityId));
    }
  }, [entityId, isItemFetched]);

  const isLoaded = () => {
    if (entityId && !isItemFetched) return false;
    return true;
  };
  const loaded = isLoaded();

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearDraftItem());
  };

  const handleSave = async () => {
    const { items, geo, ...rest } = draftItem;

    const bannersData = items.map((item) => {
      const newImage = newUploadedImages.current.find((i) => i.id === item.id);

      return {
        id: item.id,
        href: item.href,
        priority: item.priority,
        alt: item.alt,
        ...(newImage && { image: newImage.image }),
      };
    });

    const payload = {
      ...rest,
      geo: geo || geoList[0],
      items: bannersData,
    };

    formErrors.resetErrors();

    try {
      if (entityId) {
        await dispatch(
          updateSocialNetworkItem({ ...payload, id: +entityId }),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        const result = await dispatch(
          createSocialNetworkItem(payload),
        ).unwrap();

        navigate(
          `${location.pathname}?activeModal=${ModalTypes.socialNetwork}&entityId=${result.id}`,
        );

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      }
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const canBeSaved = () => {
    const hasModifiedFields = draftItem.isModified;
    const hasModifiedBanners = draftItem.items.some((b) => b.isModified);

    if (!entityId) return hasModifiedFields && hasModifiedBanners;
    return hasModifiedFields || hasModifiedBanners;
  };

  const handleTabClick = (tab: ModalPartnersTabs) => {
    if (!isEdit && tab !== ModalPartnersTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };

  const renderTab = () => {
    if (activeTab === ModalPartnersTabs.main) {
      return (
        <SocialNetworksMainTab
          newUploadedImages={newUploadedImages}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          resetErrors={formErrors.resetErrors}
          readOnly={!permissions?.update}
        />
      );
    }

    if (activeTab === ModalPartnersTabs.trail) {
      return <SocialNetworksTrailTab entityId={entityId!} />;
    }

    return null;
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header>{t("socialNetwork")}</Modal.Header>

      {loaded && (
        <Modal.Tabs
          tabs={[ModalPartnersTabs.main, ModalPartnersTabs.trail]}
          activeTab={activeTab}
          onClick={handleTabClick}
          canBeClicked={isItemFetched}
        />
      )}

      <Modal.Content className={styles.cmsFooterSocialNetworksWrapper}>
        {!loaded ? <ModalSkeleton /> : renderTab()}
      </Modal.Content>

      {loaded && activeTab !== ModalPartnersTabs.trail && permissions?.update && (
        <Modal.Footer>
          <Button isSecondary txt={t("cancel")} handleClick={handleClose} />
          <Button
            isPrimary
            handleClick={handleSave}
            txt={t("save")}
            isLoad={isItemFetching}
            disabled={!canBeSaved()}
          />
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default CmsFooterSocialNetworksEditModal;
